/**
 * TerminalSimStep.jsx
 * 체험형 레슨 스텝: 코드 수정 → 시뮬레이션 터미널 출력
 *
 * 코드에서 regex로 값을 추출하고, 로그 템플릿의 {변수명}을 치환하여
 * 애니메이션 터미널 출력을 보여줍니다.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import MonacoCodeEditor from '@components/editor/MonacoCodeEditor';
import ControlBar from '../ControlBar';
import { motion } from 'framer-motion';

function safeRegexExtract(code, regex, defaultVal) {
  try {
    const match = code.match(new RegExp(regex));
    return match ? match[1] : defaultVal;
  } catch {
    return defaultVal;
  }
}

function TerminalSimStep({ step, onBack, onNext, canGoBack }) {
  const [code, setCode] = useState(step.initialCode || '# 코드를 수정해보세요');
  const [displayedLogs, setDisplayedLogs] = useState([]);
  const logContainerRef = useRef(null);
  const language = step.language || 'python';

  // [Fix] Step 변경 시 코드 초기화
  useEffect(() => {
    setCode(step.initialCode || '# 코드를 수정해보세요');
  }, [step.initialCode]);

  // [Fix] demoData.js의 새로운 구조 지원: regex + logs (객체 배열)
  const regex = step.regex || step.extraction?.regex;
  const extractedValue = useMemo(
    () => safeRegexExtract(code, regex, '5000'),
    [code, regex]
  );

  // [Fix] logs 배열 (객체) 또는 log_templates (문자열) 지원
  const logData = step.logs || (step.log_templates || []).map(t => ({ t, c: 'text-slate-400', d: 200 }));

  const processedLogs = useMemo(() => {
    // {val} 플레이스홀더를 추출된 값으로 치환
    return logData.map(log => ({
      ...log,
      t: log.t.replace(/\{val\}/g, extractedValue)
    }));
  }, [extractedValue, logData]);

  // [Fix] Debounced log animation (딜레이 지원)
  useEffect(() => {
    setDisplayedLogs([]);
    const timers = [];
    processedLogs.forEach((log, i) => {
      const delay = log.d !== undefined ? log.d : i * 200;
      const t = setTimeout(() => {
        setDisplayedLogs(prev => [...prev, log]);
      }, delay);
      timers.push(t);
    });
    return () => timers.forEach(clearTimeout);
  }, [processedLogs]);

  // Auto-scroll terminal
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [displayedLogs]);

  return (
    <div className="relative h-full flex flex-col">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b border-slate-800/50">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-[10px] font-bold rounded uppercase tracking-wider">
            Terminal
          </span>
        </div>
        <h3 className="text-lg font-bold text-white mb-1">{step.title || '터미널 시뮬레이션'}</h3>
        {(step.desc || step.text) && <p className="text-sm text-slate-400 leading-relaxed">{step.desc || step.text}</p>}
      </div>

      {/* Split View: Editor + Terminal */}
      <div className="flex-1 flex min-h-0">
        {/* Left: Code Editor */}
        <div className="w-1/2 flex flex-col border-r border-slate-800 min-h-0">
          <div className="flex-1 min-h-0">
            <MonacoCodeEditor
              initialCode={step.initialCode || ''}
              language={language}
              onChange={setCode}
              height="100%"
              className="h-full flex flex-col"
            />
          </div>
        </div>

        {/* Right: Terminal */}
        <div className="w-1/2 bg-black flex flex-col min-h-0">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-800 text-xs font-mono text-slate-500">
            <i className="fas fa-terminal text-emerald-500 text-[10px]" />
            <span>zsh</span>
          </div>
          <div ref={logContainerRef} className="flex-1 p-4 font-mono text-sm overflow-y-auto">
            {displayedLogs.map((log, i) => {
              const text = typeof log === 'string' ? log : log.t;
              const color = log.c || 'text-slate-400';
              const isHighlight = log.highlight || text.includes('Running on') || text.includes('http://');
              return (
                <motion.div
                  key={`${i}-${text}`}
                  initial={{ opacity: 0, x: -5 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`mb-1 ${isHighlight ? 'text-emerald-400 font-semibold' : color}`}
                >
                  {text}
                </motion.div>
              );
            })}
            <div className="w-2 h-4 bg-slate-500 animate-pulse mt-2" />
          </div>
        </div>
      </div>

      <ControlBar onBack={onBack} onNext={onNext} canGoBack={canGoBack} />
    </div>
  );
}

export default TerminalSimStep;
